import { useAssetFilter } from 'components/AssetFilters/AssetFilters.hook'
import {
  ChainConfig,
  DELIVERY_TYPE,
  RARUMSALESABI,
  SIGNATURE_TTL_SECONDS,
  Stable,
  ZERO_ADDRESS,
} from 'core/constants'
import {
  normalizeUnit,
  sendAndWaitForConfirmation,
} from 'core/helpers/contract'
import { toContractNumber } from 'core/helpers/convertNumbers'
import { Sale } from 'core/logic/asset/asset.types'
import { GalleryType } from 'core/logic/gallery/gallery.types'
import { OnChainOfferType } from 'core/logic/token/token.types'
import {
  doCreateSignedOffer,
  doForwardApproval,
  observeOkenTransaction,
} from 'core/modules/firebase/service'
import useWeb3Utils from 'hooks/useWeb3Utils'
import {
  OfferForm,
  PREDEFINED_DEADLINES,
} from 'pages/Authenticated/Marketplace/CreateOffer/CreateOffer.types'
import IMarketplace, { Offers } from 'service/IMarketplace'
import OnChainBase from 'service/OnChainBase'
import { toWei } from 'web3-utils'

export default class OnchainMarketplace
  extends OnChainBase
  implements IMarketplace
{
  getSpecificOffer(offerId: string): Promise<Offers[number]> {
    throw new Error('Method not implemented.')
  }
  getQueryForCollectionOffers(): (
    page: number,
    pageSize: number | 'all',
    currItems?: Offers | undefined
  ) => Promise<{ finished: boolean; items: Offers; totalItems: number }> {
    throw new Error('Method not implemented.')
  }
  private salesContractForRead!: Contract.Sales
  private salesContractForWrite!: Contract.Sales
  private web3Utils!: ReturnType<typeof useWeb3Utils>
  private chain!: ChainConfig

  setSalesContract(contractAddress: string) {
    this.salesContractForRead = new this.web3ForRead.eth.Contract(
      RARUMSALESABI,
      contractAddress
    ) as unknown as Contract.Sales
    this.salesContractForWrite = new this.web3ForWrite.eth.Contract(
      RARUMSALESABI,
      contractAddress
    ) as unknown as Contract.Sales
  }

  setChain(web3Utils: typeof this.web3Utils, chain: ChainConfig) {
    this.web3Utils = web3Utils
    this.chain = chain
  }

  getOffersForCollection(contractAddress: string): Promise<OnChainOfferType[]> {
    const now = Date.now()
    return this.salesContractForRead.methods
      .offersInCollection(contractAddress)
      .call()
      .then((_result) => {
        const result = _result as unknown as OnChainOfferType[]
        return result
          .filter((a) => now < a.endTime)
          .filter((a) => a.active)
          .filter((a) => !a.fromTenant)
          .map((offer) => ({
            ...offer,
            deliveryOptions: {
              ...offer.deliveryOptions,
              tokenId: Number(offer.deliveryOptions.tokenId),
            },
            paymentOptions: {
              ...offer.paymentOptions,
              price: Number(offer.paymentOptions.price),
            },
          }))
          .sort((a, b) => a.paymentOptions.price - b.paymentOptions.price)
      })
  }
  async getFeeForCollection(contractAddress: string): Promise<number> {
    const result_1 = await this.salesContractForRead.methods
      .authorizedCollections(contractAddress)
      .call()
    return Number(result_1.fee) / 100
  }
  async buyOffer(sale: Sale): Promise<void> {
    const estimatedMaxGassFee = await this.calculateGas()
    await sendAndWaitForConfirmation(
      this.salesContractForWrite.methods
        .buy(sale.wallet, sale.offerId, toWei(sale.price.toString()), '1')
        .send({
          from: this.wallet!,
          maxPriorityFeePerGas: estimatedMaxGassFee,
        } as any)
    )
  }
  async createOffer(tokenContract: string, offerRequest: OfferForm) {
    const currencySymbol = Object.keys(offerRequest.amount)[0]
    const { address: paymentTokenAddress, decimals } =
      this.currency[currencySymbol]

    const price = toContractNumber(
      (offerRequest.amount[currencySymbol]?.amount ?? '0').toString(),
      decimals.toString()
    )

    let days = 30

    if (offerRequest.selectedDeadline === PREDEFINED_DEADLINES.TWO_MONTHS)
      days = 60
    if (offerRequest.selectedDeadline === PREDEFINED_DEADLINES.THREE_MONTHS)
      days = 90

    const offer = {
      supply: offerRequest.selectedQuantity,
      maxUnitsPerPurchase: offerRequest.selectedQuantity,
      startTime: Math.trunc(+new Date() / 1000),
      endTime: Math.trunc(+Date.now() / 1000 + days * 24 * 60 * 60),
      owner: this.wallet!,
      metadataURI: 'https://',
      active: true,
    }

    const paymentOptions = {
      price,
      beneficiary: this.wallet!,
      paymentToken: paymentTokenAddress,
    }

    const deliveryOptions = {
      tokenId: offerRequest.tokenId!,
      lootBoxOptionId: 0,
      nft: tokenContract,
      lootBox: ZERO_ADDRESS,
      deliveryType: DELIVERY_TYPE.TRANSFER,
    }

    const nonce = this.web3Utils.randomHex(32)
    const deadline = Math.trunc(+new Date() / 1000 + SIGNATURE_TTL_SECONDS)
    const message = {
      owner: this.wallet!,
      authorization: nonce,
      supply: offer.supply,
      nft: deliveryOptions.nft,
      tokenId: deliveryOptions.tokenId,
      price: paymentOptions.price,
      paymentToken: paymentOptions.paymentToken,
      beneficiary: paymentOptions.beneficiary,
      nonce,
      deadline,
    }

    const typedData = this.web3Utils.buildMessage(
      this.chain.id,
      this.salesContractForWrite.options.address,
      message,
      'CreateOffer',
      'GenericTypedMessage'
    )

    const { v, r, s } = await this.web3Utils.signMessage(typedData, this.wallet)

    const authorization = {
      authorization: nonce,
      deadline,
      signer: this.wallet!,
      v,
      r,
      s,
    }

    const { id } = await doCreateSignedOffer({
      offer,
      paymentOptions,
      deliveryOptions,
      authorization,
    })

    return await new Promise<string>((r) => {
      const unsubscribe = observeOkenTransaction(id, (t) => {
        if (t.status === 'MINED') {
          r(t.okenTransaction.events[0].values.index)
          unsubscribe()
        }
      })
    })
  }
  async updateOffer(offerId: Sale['offerId'], offer: OfferForm): Promise<void> {
    const estimatedMaxGassFee = await this.calculateGas()
    if ('amount' in offer) {
      const currency = Object.keys(offer.amount)[0]
      await sendAndWaitForConfirmation(
        this.salesContractForWrite.methods
          .updatePrice(
            this.wallet!,
            offerId,
            normalizeUnit(
              offer.amount[currency]!.amount,
              this.currency[currency].decimals
            ).toFixed(0)
          )
          .send({
            from: this.wallet!,
            maxPriorityFeePerGas: estimatedMaxGassFee,
          } as any)
      )
    }
    if ('selectedQuantity' in offer)
      await sendAndWaitForConfirmation(
        this.salesContractForWrite.methods
          .updateSupply(this.wallet!, offerId, offer.selectedQuantity)
          .send({
            from: this.wallet,
            maxPriorityFeePerGas: estimatedMaxGassFee,
          } as any)
      )
  }
  async removeOffer(offerId: Sale['offerId']) {
    const estimatedMaxGassFee = await this.calculateGas()
    await sendAndWaitForConfirmation(
      this.salesContractForWrite.methods
        .setOfferStatus(this.wallet!, offerId, false)
        .send({
          from: this.wallet,
          maxPriorityFeePerGas: estimatedMaxGassFee,
        } as any)
    )
  }
  async allowanceForCurrency(
    currency: CryptoCurrencySymbol,
    _allowanceRequired: number
  ): Promise<number> {
    return this.isAllowanceAuthorized(
      currency,
      _allowanceRequired,
      this.salesContractForWrite.options.address
    )
  }
  async setCurrencyAllowance(
    currency: Stable['symbol'],
    allowThisMuch: number
  ): Promise<void> {
    const currencyInfo = this.currency[currency]
    const currencyContract = currencyInfo.contract
    const estimatedMaxGassFee = await this.calculateGas()
    await sendAndWaitForConfirmation(
      currencyContract.methods
        .approve(
          this.salesContractForWrite.options.address,
          allowThisMuch.toString() + ''.padEnd(currencyInfo.decimals, '0')
        )
        .send({
          from: this.wallet!,
          maxPriorityFeePerGas: estimatedMaxGassFee,
        } as any)
    )
  }
  async canMarketplaceManageTokens(tokenContract: Contract.ERC1155) {
    return await this.canOperatorManageTokens(
      tokenContract,
      this.salesContractForRead.options.address
    )
  }
  async approveMarketplaceToManageTokens(
    galletyId: GalleryType['id'],
    tokenContract: string
  ) {
    const nonce = this.web3Utils.randomHex(32)
    const deadline = Math.trunc(+new Date() / 1000 + SIGNATURE_TTL_SECONDS)
    const message = {
      owner: this.wallet,
      operator: this.salesContractForWrite.options.address,
      approved: true,
      nonce,
      deadline,
    }
    const typedData = this.web3Utils.buildMessage(
      this.chain.id,
      tokenContract,
      message,
      'Permit',
      'https://rarum.io'
    )
    const { v, r, s } = await this.web3Utils.signMessage(typedData, this.wallet)
    const { id } = await doForwardApproval({
      galleryId: galletyId,
      operator: this.salesContractForWrite.options.address,
      approved: true,
      nonce,
      deadline,
      v,
      r,
      s,
    })
    return await new Promise<void>((r) => {
      const unsubscribe = observeOkenTransaction(id, (t) => {
        if (t.status === 'MINED') {
          r()
          unsubscribe()
        }
      })
    })
  }
}
